import PropTypes from "prop-types";
import React, { Fragment, useEffect } from "react";
import { TextField } from "../fields/TextField";
import {
  getProviderWatcher,
  loginWithPasswordWatcher,
  rememberMe,
  removeProvider,
  resetPasswordWatcher,
  setAccountNumber,
  setFetching,
  setUsername,
} from "../../redux/actions/loginactioncreators";
import { useDispatch, useSelector } from "react-redux";
import { NewFADVLoginForm } from "./NewFADVLoginForm";
import { FormattedMessage } from "react-intl";
import { ClearUserNameEntryButton } from "./NewFADVBackToUserNameEntry";
import { NewFADVPasswordField } from "../fields/NewFADVPasswordField";
import { CheckBox } from "../fields/NewFADVCheckbox";
import { ProviderDao } from "../../redux/helpers/launchProvider";
import { useIntl } from "react-intl";
import { UsernameEntry } from "./UsernameEntry";

export const LoginPrompt = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [hasBeenSubmitted, setHasBeenSubmitted] = React.useState(false);
  const [usernameNotEntered, setUsernameNotEntered] = React.useState(true);
  const [clearForm, setClearForm] = React.useState(true);
  const usernameFromRedux = useSelector(state => state.loginForm.username);
  const usernameRemembered = useSelector(state => state.loginForm.remembered ?? false);
  const provider = useSelector(state => state.loginForm.provider);
  const useAccountNumber = useSelector(state => state.loginForm.useAccountNumber);
  const autoIdpRedirectProviderData = useSelector(state => state.loginForm.autoIdpRedirectProviderData);
  const client_id = useSelector(state => state.loginForm.clientId);
  const enableAutoIdpRedirect = useSelector(state => state.loginForm.enableAutoIdpRedirect);

  const providerDao = new ProviderDao(client_id);
  providerDao.initProvider(autoIdpRedirectProviderData);
  const savedProvider = providerDao.getSavedProvider();
  const showProviderOptions = savedProvider && savedProvider.type !== "COGNITO" && enableAutoIdpRedirect === true;
  const [autolaunchProvider, setAutolaunchProvider] = React.useState(savedProvider?.autoLaunch ?? false);
  //TODO organize the handlers better, they should be grouped by what view they belong to
  const handleSubmit = (formData, errors) => {
    setHasBeenSubmitted(true);
    if (errors && Object.keys(errors).length > 0) {
      // Do not dispatch if there are errors
      return;
    }
    const { username, password, accountNumber } = formData;
    dispatch(loginWithPasswordWatcher({
      username: username,
      password: password,
      accountNumber: accountNumber,
    }));
  };

  const handleSaveUsername = () => {
    dispatch(rememberMe());
  }

  const getProvider = (formData, errors) => {
    setHasBeenSubmitted(true);
    if (errors && Object.keys(errors).length > 0) {
      // Do not dispatch if there are errors
      return;
    }
    const { username } = formData;
    dispatch(getProviderWatcher({ username }));
  };

  const handleForgotPasswordClick = (formData, _errors) => {
    const { username, accountNumber } = formData;
    dispatch(resetPasswordWatcher(
      {
        step: "RESET_PASSWORD",
        username: username.trim().toLowerCase(),
        accountNumber: accountNumber
      }
    ));
  };

  const handleLaunchProvider = (event) => {
    providerDao.launchProvider(savedProvider);
    event.preventDefault();
  };
  const handleDeleteProvider = () => {
    dispatch(setFetching(true));
    providerDao.deleteLastLoginProvider();
  };

  const toggleAutoLaunchProvider = (event) => {
    setAutolaunchProvider(event.target.checked);
    providerDao.saveLastLoginProvider(
      savedProvider,
      event.target.checked
    );
  };

  const handleClearUsername = () => {
    setUsernameNotEntered(true);
    setClearForm(true)
    setHasBeenSubmitted(false);
    dispatch(removeProvider());
  };

  const showErrors = hasBeenSubmitted;
  const providerSet = Boolean(provider);

  // Something prevents the form from auto-focusing when we clear provider
  // This workaround unmounts the form component to reset it completely
  useEffect(() => {
      if (clearForm === true) {
        setClearForm(false);
      }
  }, [clearForm]);
  if (clearForm === true) {
    return null;
  }

  if (showProviderOptions && savedProvider?.idpIdentifier && !providerSet && usernameNotEntered) {
    return (
      <NewFADVLoginForm 
        onSubmit={handleLaunchProvider}
        showErrors={showErrors}
        onChange={ (formData) => {
          dispatch(setUsername(formData.username.trim()));
          setUsernameNotEntered(formData.username.trim() === "")
        }}
        headerText={<FormattedMessage id="login.header" defaultMessage="Login" description="Login form header text" />}
        submitButtonText={<FormattedMessage id="login.providerText" defaultMessage="Login with company account" description="Login form submit button when an external auth provider is set" />}
        secondaryButtonText={<FormattedMessage id="login.deleteProvider" defaultMessage="Delete saved provider" description="Delete button for saved external auth provider" />}
        secondaryButtonHandler={handleDeleteProvider}
        onEscapeKey={handleClearUsername}
      >
        <UsernameEntry
          externalValue={usernameFromRedux}
          autoFocus={true}
          id="username"
          name="userId"
          placeholder={intl.formatMessage({
              id: "login.userIdPasswordHelperText",
              defaultMessage: "Alternatively, enter your user ID to sign in with a password",
              description: "Helper text displayed in user id input field suggesting a user can login with password"
            }
          )}
        />
        <Fragment>
          <CheckBox
            id="autoLaunchProvider"
            onChange={toggleAutoLaunchProvider}
            name="autoLaunchProvider"
            checked={autolaunchProvider}
          />
          <label htmlFor="autoLaunchProvider">
            <FormattedMessage id={"login.autoLaunchProviderText"} defaultMessage="Enable automatic company login" description="Auto-launch external-auth-provider checkbox label" />
          </label>
        </Fragment>
      </NewFADVLoginForm>
    )
  }

  if (!providerSet) {
    return (
      <NewFADVLoginForm
        onSubmit={getProvider}
        onChange={(formData) => {
          dispatch(setUsername(formData.username.trim()));
        }}
        showErrors={showErrors}
        requiredFields={["username"]}
        headerText={<FormattedMessage id="login.header" defaultMessage="Login" description="Login form header text" />}
        submitButtonText={<FormattedMessage id='login.next' defaultMessage="Continue" description="Login form submit button" />}
        onEscapeKey={handleClearUsername}
      >
        <UsernameEntry
          externalValue={usernameFromRedux}
          id="username"
          name="getProviderUsername"
          autoFocus={true}
          adornment={showProviderOptions ? <ClearUserNameEntryButton onClick={handleClearUsername} /> : null}
        />
      </NewFADVLoginForm>
    );
  }

  return (
    <NewFADVLoginForm
      onSubmit={handleSubmit}
      onChange={(formData) => {
        if (formData.accountNumber && formData.accountNumber.length > 0) {
          dispatch(setAccountNumber(formData.accountNumber));
        }
      }}
      showErrors={showErrors}
      requiredFields={["username", "password"].concat(useAccountNumber ? ["accountNumber"] : [])}
      headerText={<FormattedMessage id="login.header" defaultMessage="Login" description="Login form header text" />}
      submitButtonText={<FormattedMessage id='login.signIn' defaultMessage="Sign In" description="Login form submit button" />}
      secondaryButtonText={<FormattedMessage id="login.forgotPasswordLinkText" defaultMessage="Forgot Password?" description="Text for the forgot password link in the login form" />}
      secondaryButtonHandler={handleForgotPasswordClick}
      secondaryButtonId="forgotYourPassword"
      onEscapeKey={handleClearUsername}
    >
      <UsernameEntry
        externalValue={usernameFromRedux}
        id="username"
        name="username"
        disabled={true}
        adornment={<ClearUserNameEntryButton onClick={handleClearUsername} />}
      />
      <NewFADVPasswordField
        label={<FormattedMessage id="login.password" defaultMessage="Password" description="Login form user input" />}
        autoFocus={true}
        id="password"
        type="password"
      />
      <TextField
        show={useAccountNumber}
        label={<FormattedMessage id="login.clientAccountId" defaultMessage="Client ID" description="Login form user input" />}
        id="accountNumber"
      />
      <CheckBox wrapperClassName="mb-1" id="saveUsername" checked={usernameRemembered} onClick={handleSaveUsername} labelText={<FormattedMessage id={"login.saveUsername"} defaultMessage="Remember me" description="Label for the remember me checkbox in the login form" />} />
    </NewFADVLoginForm>
  );
}

LoginPrompt.propTypes = {
  handleInputChange: PropTypes.func.isRequired,
  formData: PropTypes.object
}
